import { cva } from 'class-variance-authority'

// Control geometry — exported so other primitives (e.g. Skeleton) can align to Button without
// duplicating the values. `buttonRadius`'s value must match the `rounded-20` in the base string
// below; the SSOT test in button.test.tsx catches any divergence in either direction.
export const buttonHeight = { small: 'h-[32px]', medium: 'h-[40px]', large: 'h-[48px]' } as const
export const buttonRadius = 'rounded-20'

const buttonVariants = cva(
  `
    group/button inline-flex shrink-0 items-center justify-center gap-30 rounded-20 border text-20 leading-50 font-500
    tracking-20 whitespace-nowrap no-underline transition-[color,background-color,border-color,box-shadow,scale]
    motion-color outline-none select-none
    focus-visible:border-line-base-input-focus focus-visible:ring-3 focus-visible:ring-line-base-input-focus/50
    active:scale-[0.96]
    aria-invalid:border-line-danger aria-invalid:ring-3 aria-invalid:ring-line-danger/20
    data-disabled:pointer-events-none
    [&_svg]:pointer-events-none [&_svg]:shrink-0
  `,
  {
    variants: {
      variant: {
        primary: `
          border-transparent bg-surface-base-primary text-content-base-primary shadow-down-05-subtle
          hover:bg-surface-base-primary-hover
          active:bg-surface-base-primary-active
          data-disabled:bg-surface-base-disable data-disabled:text-content-base-disable data-disabled:shadow-none
        `,
        secondary: `
          border-line-base-main bg-surface-base-secondary text-content-base-secondary shadow-down-05-subtle
          hover:bg-surface-base-secondary-hover
          active:bg-surface-base-secondary-active
          data-disabled:border-line-base-main-disable data-disabled:bg-surface-base-disable
          data-disabled:text-content-base-disable data-disabled:shadow-none
        `,
        tertiary: `
          border-transparent bg-surface-base-tertiary text-content-base-tertiary
          hover:bg-surface-base-tertiary-hover
          active:bg-surface-base-tertiary-active
          data-disabled:bg-surface-base-disable data-disabled:text-content-base-disable
        `,
        text: `
          border-transparent bg-transparent text-content-base-main underline-offset-4
          hover:underline
          data-disabled:text-content-base-disable data-disabled:no-underline
        `,
      },
      size: {
        small: `
          ${buttonHeight.small}
          px-40
          [&_svg:not([class*='size-'])]:size-[16px]
        `,
        medium: `
          ${buttonHeight.medium}
          px-50
          [&_svg:not([class*='size-'])]:size-[16px]
        `,
        large: `
          ${buttonHeight.large}
          px-50
          [&_svg:not([class*='size-'])]:size-6
        `,
        'icon-small': `
          size-[32px]
          [&_svg:not([class*='size-'])]:size-[16px]
        `,
        'icon-medium': `
          size-[40px]
          [&_svg:not([class*='size-'])]:size-5
        `,
        'icon-large': `
          size-[48px]
          [&_svg:not([class*='size-'])]:size-6
        `,
      },
      mobileSize: {
        small: `
          max-md:h-[32px] max-md:w-auto max-md:px-40
          max-md:[&_svg:not([class*='size-'])]:size-[16px]
        `,
        medium: `
          max-md:h-[40px] max-md:w-auto max-md:px-50
          max-md:[&_svg:not([class*='size-'])]:size-[16px]
        `,
        large: `
          max-md:h-[48px] max-md:w-auto max-md:px-50
          max-md:[&_svg:not([class*='size-'])]:size-[16px]
        `,
        'icon-small': `
          max-md:size-[32px] max-md:px-0
          max-md:[&_svg:not([class*='size-'])]:size-[16px]
        `,
        'icon-medium': `
          max-md:size-[40px] max-md:px-0
          max-md:[&_svg:not([class*='size-'])]:size-5
        `,
        'icon-large': `
          max-md:size-[48px] max-md:px-0
          max-md:[&_svg:not([class*='size-'])]:size-6
        `,
      },
      // `intent.base` is intentionally empty — the base styling lives in `variant.*`.
      // Non-base intents override surface/border/text via the `compoundVariants` block below.
      // Disabled state collapses to the base disabled tokens for ALL intents (gotcha #1 below).
      //
      // Disabled is styled via `data-disabled:` (Base UI's data attribute), NOT the native `:disabled`
      // pseudo-class. Reasons: (1) it matches the rest of pharaoh-next (Switch/Input already use
      // `data-disabled:`); (2) the LoadingButton pattern renders the button with `focusableWhenDisabled`,
      // so Base UI emits `aria-disabled` + `data-disabled` (and keeps it focusable) WITHOUT the native
      // `disabled` attribute — `:disabled` would never match there, but `data-disabled` is emitted in
      // both the native-disabled and focusable-disabled cases.
      intent: {
        base: '',
        danger: '',
        success: '',
        warning: '',
        info: '',
        decorative: '',
      },
    },
    // Token mapping mirrors pharaoh v1 (`packages/libs/pharaoh/src/components/button/button.tsx`
    // tables `{primary,secondary,tertiary}ColorsPerButtonStylePerTheme` + `textColorsPerButtonStylePerTheme`).
    // The v1 quirks are preserved verbatim here:
    //   #1 disabled collapses to base disabled tokens for all intents (inherited from variant.*)
    //   #2 tertiary text uses `secondary`-level content (not `tertiary`)
    //   #3 decorative loses shadow on hover/active (idle keeps it)
    //   #4 decorative tertiary reuses `bg-decorative-hover` on both hover AND active
    compoundVariants: [
      // primary × {intent} — colored idle/hover/active surface. Text stays base-primary (white)
      // except for `decorative` which uses `decorative-primary`. Disabled inherited.
      {
        variant: 'primary',
        intent: 'danger',
        class: `
          bg-surface-danger-primary
          hover:bg-surface-danger-primary-hover
          active:bg-surface-danger-primary-active
        `,
      },
      {
        variant: 'primary',
        intent: 'success',
        class: `
          bg-surface-success-primary
          hover:bg-surface-success-primary-hover
          active:bg-surface-success-primary-active
        `,
      },
      {
        variant: 'primary',
        intent: 'warning',
        class: `
          bg-surface-warning-primary
          hover:bg-surface-warning-primary-hover
          active:bg-surface-warning-primary-active
        `,
      },
      {
        variant: 'primary',
        intent: 'info',
        class: `
          bg-surface-info-primary
          hover:bg-surface-info-primary-hover
          active:bg-surface-info-primary-active
        `,
      },
      {
        variant: 'primary',
        intent: 'decorative',
        class: `
          bg-surface-decorative-primary text-content-decorative-primary
          hover:bg-surface-decorative-primary-hover
          active:bg-surface-decorative-primary-active
        `,
      },
      // secondary × {intent} — colored surface + border, intent `secondary`-level text. Disabled inherited.
      {
        variant: 'secondary',
        intent: 'danger',
        class: `
          border-line-danger bg-surface-danger-secondary text-content-danger-secondary
          hover:border-line-danger-hover hover:bg-surface-danger-secondary-hover
          active:border-line-danger-active active:bg-surface-danger-secondary-active
        `,
      },
      {
        variant: 'secondary',
        intent: 'success',
        class: `
          border-line-success bg-surface-success-secondary text-content-success-secondary
          hover:border-line-success-hover hover:bg-surface-success-secondary-hover
          active:border-line-success-active active:bg-surface-success-secondary-active
        `,
      },
      {
        variant: 'secondary',
        intent: 'warning',
        class: `
          border-line-warning bg-surface-warning-secondary text-content-warning-secondary
          hover:border-line-warning-hover hover:bg-surface-warning-secondary-hover
          active:border-line-warning-active active:bg-surface-warning-secondary-active
        `,
      },
      {
        variant: 'secondary',
        intent: 'info',
        class: `
          border-line-info bg-surface-info-secondary text-content-info-secondary
          hover:border-line-info-hover hover:bg-surface-info-secondary-hover
          active:border-line-info-active active:bg-surface-info-secondary-active
        `,
      },
      {
        variant: 'secondary',
        intent: 'decorative',
        class: `
          border-line-decorative bg-surface-decorative-secondary text-content-decorative-secondary
          hover:border-line-decorative-hover hover:bg-surface-decorative-hover
          active:border-line-decorative-active active:bg-surface-decorative-active
        `,
      },
      // tertiary × {intent} — intent tertiary surface, intent `secondary`-level text (gotcha #2).
      // Disabled inherited.
      {
        variant: 'tertiary',
        intent: 'danger',
        class: `
          bg-surface-danger-tertiary text-content-danger-secondary
          hover:bg-surface-danger-tertiary-hover
          active:bg-surface-danger-tertiary-active
        `,
      },
      {
        variant: 'tertiary',
        intent: 'success',
        class: `
          bg-surface-success-tertiary text-content-success-secondary
          hover:bg-surface-success-tertiary-hover
          active:bg-surface-success-tertiary-active
        `,
      },
      {
        variant: 'tertiary',
        intent: 'warning',
        class: `
          bg-surface-warning-tertiary text-content-warning-secondary
          hover:bg-surface-warning-tertiary-hover
          active:bg-surface-warning-tertiary-active
        `,
      },
      {
        variant: 'tertiary',
        intent: 'info',
        class: `
          bg-surface-info-tertiary text-content-info-secondary
          hover:bg-surface-info-tertiary-hover
          active:bg-surface-info-tertiary-active
        `,
      },
      // Gotcha #4 — decorative tertiary reuses `bg-decorative-hover` on both hover AND active
      // (v1 quirk). Text stays at `decorative-tertiary` (not `-secondary`).
      {
        variant: 'tertiary',
        intent: 'decorative',
        class: `
          bg-surface-decorative-tertiary text-content-decorative-tertiary
          hover:bg-surface-decorative-hover
          active:bg-surface-decorative-hover
        `,
      },
      // text × {intent} — bare text in the intent's `secondary`-level color. No surface, no border.
      { variant: 'text', intent: 'danger', class: 'text-content-danger-secondary' },
      { variant: 'text', intent: 'success', class: 'text-content-success-secondary' },
      { variant: 'text', intent: 'warning', class: 'text-content-warning-secondary' },
      { variant: 'text', intent: 'info', class: 'text-content-info-secondary' },
      { variant: 'text', intent: 'decorative', class: 'text-content-decorative-secondary' },
      // Gotcha #3 — decorative loses shadow on hover/active (idle keeps `shadow-down-05-subtle`
      // from variant.{primary,secondary}). Harmless no-op on tertiary/text which have no shadow.
      {
        intent: 'decorative',
        class: `
          hover:shadow-none
          active:shadow-none
        `,
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
      intent: 'base',
    },
  },
)

export { buttonVariants }
